import { useCallback, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { formatPhone } from '@jupi/shared'
import { formatDateTime, formatRange } from '@jupi/ui'
import type { Booking } from '../../api/queries'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useBookingAccess } from './bookingAccess'
import { BOOKING_TYPE_ICONS, typeOf } from './bookingType'
import { standbyPlaceLabel } from './standbyPlace'

// Who has the aircraft and how to reach them: name, phone (a tel: link, so a
// tap dials on a phone) and the booking's notes. The phone is already null in
// the API when the member keeps their details private.
export function BookingSummary({ booking }: { booking: Booking }) {
  const { t } = useTranslation()
  return (
    <Stack spacing={0.5} sx={{ minWidth: 200, maxWidth: 300 }}>
      <Typography variant="subtitle2">
        <Icon icon={BOOKING_TYPE_ICONS[typeOf(booking)]} width={16} style={{ verticalAlign: -3, marginRight: 4 }} />
        {booking.registration} · {booking.displayName}
        {booking.kind === 'option' ? ` (${t('booking.option')})` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {formatRange(booking.startsAt, booking.endsAt)}
      </Typography>
      {booking.phone ? (
        <Link
          href={`tel:${booking.phone.replace(/[\s()-]+/g, '')}`}
          variant="body2"
          underline="hover"
          // Not a click on the calendar event underneath.
          onClick={(event) => event.stopPropagation()}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, py: 0.5 }}
        >
          <Icon icon="mdi:phone" width={16} />
          {formatPhone(booking.phone)}
        </Link>
      ) : null}
      {booking.instructorName ? (
        <Typography variant="body2">
          {t('booking.instructor')}: {booking.instructorName}
        </Typography>
      ) : null}
      {booking.kind === 'option' && booking.queuePosition ? (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {standbyPlaceLabel(t, booking.queuePosition)}
        </Typography>
      ) : null}
      <Typography variant="caption" color="text.secondary">
        {booking.createdByName && booking.createdByName !== booking.displayName
          ? t('booking.bookedByAt', { name: booking.createdByName, when: formatDateTime(booking.createdAt) })
          : t('booking.bookedAtWhen', { when: formatDateTime(booking.createdAt) })}
      </Typography>
      {booking.comment ? (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          <strong>{t('booking.comment')}:</strong> {booking.comment}
        </Typography>
      ) : null}
    </Stack>
  )
}

// Hover (a mouse only: touch has the click popover) shows the summary. The
// tooltip stays open while the pointer is on it, so the phone link can be used.
export function BookingHover({ booking, children }: { booking: Booking; children: ReactElement }) {
  return (
    <Tooltip
      title={<BookingSummary booking={booking} />}
      enterDelay={400}
      enterNextDelay={400}
      disableTouchListener
      slotProps={{
        tooltip: {
          sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 4, border: 1, borderColor: 'divider', p: 1.5 },
        },
      }}
    >
      {children}
    </Tooltip>
  )
}

// Click or tap on a booking: the same summary in a popover, with a button to
// open the booking itself when it is the member's to open (their own, a
// lesson they teach, or any for administrators and maintenance). With a
// mouse, hovering already shows everything others may see, so a click on
// someone else's booking does nothing; on touch, where there is no hover,
// the popover is the only way to see it, without the button.
export function useBookingPeek(onOpenBooking?: (booking: Booking) => void) {
  const { t } = useTranslation()
  const { canOpen } = useBookingAccess()
  const hasHover = useMediaQuery('(hover: hover)')
  const [peek, setPeek] = useState<{ booking: Booking; anchor: HTMLElement } | null>(null)
  const open = useCallback(
    (booking: Booking, anchor: HTMLElement) => {
      if (hasHover && !canOpen(booking)) return
      setPeek({ booking, anchor })
    },
    [hasHover, canOpen],
  )
  const close = () => setPeek(null)

  const element = (
    <Popover
      open={peek !== null}
      anchorEl={peek?.anchor}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      {peek ? (
        <Box sx={{ p: 2 }}>
          <BookingSummary booking={peek.booking} />
          {onOpenBooking && canOpen(peek.booking) ? (
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 1.5 }}
              onClick={() => {
                const booking = peek.booking
                close()
                onOpenBooking(booking)
              }}
            >
              {t('booking.openBooking')}
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Popover>
  )
  return { open, element }
}
